/**
 * Detection + recovery for "stale deploy" chunk-load errors.
 *
 * PROBLEM (2026-09): when a new deployment activates the service worker
 * (skipWaiting + clientsClaim), the old precache is cleaned up and the old
 * hashed asset URLs no longer exist on the server. A long-open window still
 * running the old bundle then fails to lazy-import a view whose chunk was
 * never loaded — React.lazy throws and the route shows the error fallback
 * ("Etwas ist schiefgelaufen"). Only a manual F5 used to fix it.
 *
 * FIX: detect these errors in error-boundary fallbacks and reload the page
 * automatically (rate-limited). After the reload the NEW service worker
 * controls the page and serves the NEW precached index.html + chunks, so the
 * app comes back working — the equivalent of the user's manual F5.
 *
 * ESCALATION LADDER (2026-10-15, incident: coworker stuck on chat chunk):
 * A plain reload could NOT heal every client. When the browser (installed
 * PWA window) kept a STALE SERVICE-WORKER PRECACHE, the SW served the old
 * index.html cache-first on EVERY reload — so the fresh index.html with the
 * new chunk hashes never reached the app, and the failing chunk request kept
 * returning the SPA-fallback HTML (text/html MIME error). Manual cache
 * deletion fixed it (owner-confirmed). Therefore:
 *
 *   1st failure  → retry in-flight imports → ONE plain reload
 *                  (heals server-side staleness / stale HTTP-cached HTML)
 *   failure persists
 *   after that reload (the sessionStorage guard is still set — the strongest
 *                  available signal that the last reload did NOT heal) →
 *                  purge ALL Cache Storage entries + unregister service
 *                  workers, then reload. The next page load re-registers a
 *                  fresh worker (pwaAutoUpdate.ts), which rebuilds the
 *                  precache with the CURRENT hashes.
 *   failure persists
 *   after the purge → both guards active → show the manual "reload" button.
 *
 * Cache Storage is safe to purge wholesale: the Workbox precache is the only
 * consumer in this app (localStorage/sessionStorage/IndexedDB are untouched).
 */

import { captureException } from '@sentry/react';

/**
 * Matches the chunk/module load failure messages of the major browsers:
 * - Chrome/Edge: "Failed to fetch dynamically imported module: <url>"
 * - Firefox:     "error loading dynamically imported module"
 * - Safari:      "Importing a module script failed."
 * - Vite/webpack style: "Loading CSS chunk <name> failed."
 *
 * AND the two variants a stale deploy produces when the server answers a
 * missing JS asset with the SPA fallback document (text/html) or with 404
 * (Sentry ADAPTHUB-4Q / 92 / 93 / 94 / 95):
 * - Chrome (static module script): "'text/html' is not a valid JavaScript MIME type."
 * - Chrome (static module script): "Failed to load module script: … 404 …"
 *
 * Without the second group the recovery ladder stayed silent for exactly the
 * variant that a stale index.html produces — the client then sat on a broken
 * screen until the user hit F5 manually.
 */
export function isChunkLoadError(error: unknown): boolean {
  const message =
    (error instanceof Error && error.message) ||
    (typeof error === 'string' ? error : '') ||
    (error as any)?.message ||
    '';
  return /failed to fetch dynamically imported module|importing a module script failed|error loading dynamically imported module|loading .* chunk .* failed|failed to load module script|is not a valid javascript mime type/i.test(
    message,
  );
}

/**
 * Marker message of the "automatic recovery is exhausted" signal.
 *
 * The chunk-load error itself is auto-healed (reload → SW/cache purge) and is
 * therefore filtered out of Sentry (src/main.tsx). This marker is the ONE
 * chunk-related event that must stay visible: it means the ladder ran out and
 * the user is stuck on the manual "reload" button.
 */
export const CHUNK_RECOVERY_EXHAUSTED_MARKER = 'chunk-load-recovery-exhausted';

const RELOAD_GUARD_KEY = 'adapthub.chunkReloadAt';
const RELOAD_GUARD_MS = 60_000; // max one auto-reload per minute per tab
const PURGE_GUARD_KEY = 'adapthub.chunkPurgeAt';
const PURGE_GUARD_MS = 5 * 60_000; // one purge per 5 minutes per tab
const EXHAUSTED_REPORT_KEY = 'adapthub.chunkExhaustedReportedAt';

/**
 * Delete every Cache Storage entry and unregister all service workers.
 *
 * This is the programmatic equivalent of the owner's manual fix
 * (F12 → Application → Storage → Clear site data → reload): it removes the
 * STALE WORKBOX PRECACHE that kept serving an old index.html even after
 * reloads. localStorage / sessionStorage / IndexedDB are NOT touched — only
 * the SW Cache Storage, which in this app is used exclusively by the
 * vite-plugin-pwa precache (vite.config.ts).
 *
 * Best-effort: failures are swallowed so the reload still happens — a
 * half-completed purge is strictly better than no recovery.
 */
async function purgeServiceWorkerCache(): Promise<void> {
  // Two INDEPENDENT try blocks: an SW-API failure must never prevent the
  // cache deletion (and vice versa) — each step is best-effort on its own.
  try {
    const registrations = await navigator.serviceWorker?.getRegistrations?.();
    for (const reg of registrations ?? []) {
      try {
        await reg.unregister();
      } catch {
        // Best-effort — continue purging the remaining registrations.
      }
    }
  } catch {
    // Service-worker API unavailable (older browser, private mode).
  }
  try {
    const keys = await caches?.keys?.();
    for (const key of keys ?? []) {
      try {
        await caches.delete(key);
      } catch {
        // Best-effort — continue deleting the remaining caches.
      }
    }
  } catch {
    // Cache Storage unavailable — nothing to purge.
  }
}
/**
 * Report that the automatic recovery ladder is exhausted (once per tab
 * session).
 *
 * The plain chunk-load error is filtered out of Sentry — the ladder heals it.
 * This marker is the opposite: it says the ladder ran out (a reload ran AND a
 * service-worker/cache purge ran, yet the client is still failing) and the
 * user is sitting on the manual "reload" button. That IS actionable, so it
 * must reach Sentry even though its sibling errors do not.
 */
function reportChunkRecoveryExhausted(): void {
  try {
    if (sessionStorage.getItem(EXHAUSTED_REPORT_KEY)) return;
    sessionStorage.setItem(EXHAUSTED_REPORT_KEY, String(Date.now()));
  } catch {
    // Private mode may block storage — report anyway.
  }
  try {
    captureException(new Error(CHUNK_RECOVERY_EXHAUSTED_MARKER));
  } catch {
    // Sentry not initialized (or no DSN) — never break the recovery path.
  }
}

/**
 * Attempt one rate-limited automatic recovery reload.
 * Returns true when a recovery has been scheduled (caller should show a
 * "loading new version" spinner instead of an error screen), false when the
 * guard blocks it (recent recovery / offline) and the caller should show a
 * manual "update now" button instead.
 */
export function recoverFromChunkError(): boolean {
  if (typeof window === 'undefined') return false;
  if (!navigator.onLine) return false;
  const now = Date.now();
  const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
  if (now - last < RELOAD_GUARD_MS) {
    // The reload guard blocks a second auto-reload. When a SW/cache purge
    // already ran in this session as well, the whole ladder is exhausted →
    // the UI shows the manual reload button and Sentry gets the marker.
    if (last > 0 && !purgeNeeded()) reportChunkRecoveryExhausted();
    return false;
  }
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(now));
  } catch {
    // Private mode may block storage — reload anyway, the guard is best-effort.
  }
  // ESCALATION: a previous recovery in this tab session (sessionStorage
  // survives reloads) means the plain reload already FAILED to heal the
  // client — the stale service-worker precache class a reload cannot fix
  // (incident 2026-10-15: chat chunk served as text/html from the SW cache
  // on every reload until manual cache deletion). Escalate to the SW/cache
  // purge, rate-limited by its own guard. The purge runs BEFORE the reload;
  // the next page load re-registers a fresh worker (pwaAutoUpdate.ts) and
  // rebuilds the precache with the CURRENT asset hashes.
  if (last > 0 && purgeNeeded()) {
    try {
      sessionStorage.setItem(PURGE_GUARD_KEY, String(now));
    } catch {
      // Best-effort — purge anyway.
    }
    void purgeServiceWorkerCache().finally(() => scheduleReload());
  } else {
    scheduleReload();
  }
  return true;
}

/** True when the SW/cache purge path should run for this recovery. */
function purgeNeeded(): boolean {
  const lastPurge = Number(sessionStorage.getItem(PURGE_GUARD_KEY) || 0);
  return Date.now() - lastPurge >= PURGE_GUARD_MS;
}

function scheduleReload() {
  // Tiny delay so React can paint the "updating" spinner before the reload.
  window.setTimeout(() => window.location.reload(), 150);
}
